import React, { useState, useEffect } from 'react';
import { Save, Copy, Download, Mail, Link, Settings, Plus, Trash2, Calendar, DollarSign, Shield, FileText, Eye, Edit } from 'lucide-react';

const CancellationPolicy = () => {
  const [activeTab, setActiveTab] = useState('periods');
  const [selectedProperty, setSelectedProperty] = useState('villa-azure');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [periods, setPeriods] = useState([
    {
      id: 1,
      name: 'Haute Saison',
      dateRange: '15 juin - 15 septembre',
      color: 'red',
      rules: [
        { daysFrom: 60, daysTo: 31, refundPercent: 100 },
        { daysFrom: 30, daysTo: 15, refundPercent: 50 },
        { daysFrom: 14, daysTo: 0, refundPercent: 0 }
      ]
    },
    {
      id: 2,
      name: 'Basse Saison',
      dateRange: '16 septembre - 14 juin',
      color: 'blue',
      rules: [
        { daysFrom: 45, daysTo: 21, refundPercent: 100 },
        { daysFrom: 20, daysTo: 7, refundPercent: 75 },
        { daysFrom: 6, daysTo: 0, refundPercent: 25 }
      ]
    }
  ]);

  const [specialConditions, setSpecialConditions] = useState({
    forceMajeure: { enabled: true, fullRefund: true },
    flightCancellation: { enabled: true, fullRefund: false, refundPercent: 80 },
    medicalReasons: { enabled: true, fullRefund: false, refundPercent: 90 },
    covidRestrictions: { enabled: true, fullRefund: true },
    naturalDisaster: { enabled: true, fullRefund: true }
  });

  const [paymentStructure, setPaymentStructure] = useState({
    deposit: { percent: 30, dueAt: 'booking' },
    balance: { percent: 70, dueAt: '30 days before arrival' },
    securityDeposit: { amount: 2500, refundable: true }
  });

  const [refundSimulation, setRefundSimulation] = useState({
    totalAmount: 15000,
    daysBeforeArrival: 20,
    selectedPeriod: 'haute-saison'
  });

  // Templates prédéfinis
  const policyTemplates = [
    {
      id: 'flexible',
      name: 'Flexible',
      description: 'Politique d\'annulation souple',
      icon: '😊',
      rules: 'Remboursement complet jusqu\'à 7 jours avant'
    },
    {
      id: 'moderate',
      name: 'Modérée',
      description: 'Équilibre entre flexibilité et protection',
      icon: '⚖️',
      rules: 'Remboursement dégressif selon les délais'
    },
    {
      id: 'strict',
      name: 'Stricte',
      description: 'Protection maximale du propriétaire',
      icon: '🔒',
      rules: 'Remboursement limité en cas d\'annulation'
    },
    {
      id: 'luxury',
      name: 'Luxe Premium',
      description: 'Adaptée aux villas de prestige',
      icon: '💎',
      rules: 'Conditions spéciales pour clientèle VIP'
    }
  ];

  const calculateRefund = () => {
    const { totalAmount, daysBeforeArrival, selectedPeriod } = refundSimulation;
    const period = periods.find(p => p.name.toLowerCase().includes(selectedPeriod.split('-')[0]));
    
    if (!period) return 0;

    const applicableRule = period.rules.find(rule => 
      daysBeforeArrival >= rule.daysTo && daysBeforeArrival <= rule.daysFrom
    );

    if (!applicableRule) return 0;

    return (totalAmount * applicableRule.refundPercent) / 100;
  };

  const addNewPeriod = () => {
    const newPeriod = {
      id: Date.now(),
      name: 'Nouvelle Période',
      dateRange: 'À définir',
      color: 'green',
      rules: [
        { daysFrom: 30, daysTo: 15, refundPercent: 100 },
        { daysFrom: 14, daysTo: 0, refundPercent: 50 }
      ]
    };
    setPeriods([...periods, newPeriod]);
  };

  const addRuleToPeriod = (periodId) => {
    setPeriods(periods.map(period => 
      period.id === periodId 
        ? {
            ...period,
            rules: [...period.rules, { daysFrom: 7, daysTo: 0, refundPercent: 0 }]
          }
        : period
    ));
  };

  const updateRule = (periodId, ruleIndex, field, value) => {
    setPeriods(periods.map(period => 
      period.id === periodId 
        ? {
            ...period,
            rules: period.rules.map((rule, index) => 
              index === ruleIndex ? { ...rule, [field]: parseInt(value) } : rule
            )
          }
        : period
    ));
  };

  const removeRule = (periodId, ruleIndex) => {
    setPeriods(periods.map(period => 
      period.id === periodId 
        ? {
            ...period,
            rules: period.rules.filter((_, index) => index !== ruleIndex)
          }
        : period
    ));
  };

  const toggleSpecialCondition = (condition) => {
    setSpecialConditions(prev => ({
      ...prev,
      [condition]: {
        ...prev[condition],
        enabled: !prev[condition].enabled
      }
    }));
  };

  const PolicyTimeline = ({ period }) => (
    <div className="timeline mt-4 p-4 bg-gray-50 rounded-lg">
      <h5 className="font-medium text-gray-700 mb-3">Visualisation - {period.name}</h5>
      <div className="flex items-center space-x-2">
        {period.rules.map((rule, index) => (
          <div key={index} className="flex-1">
            <div className={`h-8 bg-${period.color}-${200 + index * 100} rounded flex items-center justify-center text-sm font-medium text-${period.color}-800`}>
              {rule.refundPercent}%
            </div>
            <div className="text-xs text-gray-600 text-center mt-1">
              {rule.daysFrom}-{rule.daysTo} jours
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="cancellation-policy">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Politique d'Annulation</h1>
            <p className="text-gray-600 mt-1">Configurez les conditions d'annulation pour vos propriétés</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <select 
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="villa-azure">Villa Côte d'Azur</option>
              <option value="chateau-provence">Château Provence</option>
              <option value="villa-toscane">Villa Toscane</option>
            </select>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Save size={18} />
              <span>Sauvegarder</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1">
        {/* Sidebar Templates */}
        <div className="templates-sidebar w-80 bg-white border-r border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Modèles Prédéfinis</h3>
          
          <div className="space-y-3">
            {policyTemplates.map((template) => (
              <div
                key={template.id}
                className={`template-card p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedTemplate === template.id ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-2xl">{template.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-800">{template.name}</h4>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500">{template.rules}</p>
              </div>
            ))}
          </div>

          <button className="w-full mt-6 flex items-center justify-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700">
            <Plus size={18} />
            <span>Créer un modèle</span>
          </button>
        </div>

        {/* Contenu principal */}
        <div className="main-content flex-1">
          {/* Onglets */}
          <div className="tabs border-b border-gray-200 bg-white px-6">
            <nav className="flex space-x-8">
              {[
                { id: 'periods', label: 'Par Périodes', icon: Calendar },
                { id: 'conditions', label: 'Conditions Spéciales', icon: Shield },
                { id: 'payments', label: 'Paiements & Acomptes', icon: DollarSign },
                { id: 'insurance', label: 'Assurances', icon: FileText },
                { id: 'preview', label: 'Aperçu Client', icon: Eye }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="tab-content p-6">
            {/* Onglet Par Périodes */}
            {activeTab === 'periods' && (
              <div className="periods-tab">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800">Configuration par Périodes</h3>
                  <button
                    onClick={addNewPeriod}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus size={18} />
                    <span>Ajouter une période</span>
                  </button>
                </div>

                <div className="space-y-6">
                  {periods.map((period) => (
                    <div key={period.id} className="period-card bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 bg-${period.color}-500 rounded-full`}></div>
                          <div>
                            <input
                              type="text"
                              value={period.name}
                              onChange={(e) => {
                                setPeriods(periods.map(p => 
                                  p.id === period.id ? { ...p, name: e.target.value } : p
                                ));
                              }}
                              className="text-lg font-semibold text-gray-800 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded px-2"
                            />
                            <input
                              type="text"
                              value={period.dateRange}
                              onChange={(e) => {
                                setPeriods(periods.map(p => 
                                  p.id === period.id ? { ...p, dateRange: e.target.value } : p
                                ));
                              }}
                              className="text-sm text-gray-600 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded px-2"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => setPeriods(periods.filter(p => p.id !== period.id))}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="rules-section">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-gray-700">Règles d'annulation</h4>
                          <button
                            onClick={() => addRuleToPeriod(period.id)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Ajouter une règle
                          </button>
                        </div>

                        <div className="space-y-3">
                          {period.rules.map((rule, ruleIndex) => (
                            <div key={ruleIndex} className="rule-row flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">De</span>
                                <input
                                  type="number"
                                  value={rule.daysFrom}
                                  onChange={(e) => updateRule(period.id, ruleIndex, 'daysFrom', e.target.value)}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                />
                                <span className="text-sm text-gray-600">à</span>
                                <input
                                  type="number"
                                  value={rule.daysTo}
                                  onChange={(e) => updateRule(period.id, ruleIndex, 'daysTo', e.target.value)}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                />
                                <span className="text-sm text-gray-600">jours avant l'arrivée</span>
                              </div>

                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Remboursement:</span>
                                <input
                                  type="number"
                                  value={rule.refundPercent}
                                  onChange={(e) => updateRule(period.id, ruleIndex, 'refundPercent', e.target.value)}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                />
                                <span className="text-sm text-gray-600">%</span>
                              </div>

                              <button
                                onClick={() => removeRule(period.id, ruleIndex)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>

                        <PolicyTimeline period={period} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Conditions Spéciales */}
            {activeTab === 'conditions' && (
              <div className="conditions-tab">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Conditions Spéciales</h3>
                
                <div className="space-y-4">
                  {Object.entries(specialConditions).map(([key, condition]) => (
                    <div key={key} className="condition-item bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={condition.enabled}
                              onChange={() => toggleSpecialCondition(key)}
                              className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {key === 'forceMajeure' && 'Cas de force majeure (guerre, catastrophe naturelle, etc.)'}
                              {key === 'flightCancellation' && 'Annulation de vol par la compagnie'}
                              {key === 'medicalReasons' && 'Raisons médicales justifiées'}
                              {key === 'covidRestrictions' && 'Restrictions liées au COVID-19'}
                              {key === 'naturalDisaster' && 'Catastrophe naturelle'}
                            </p>
                          </div>
                        </div>

                        {condition.enabled && (
                          <div className="flex items-center space-x-3">
                            {!condition.fullRefund && (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Remboursement:</span>
                                <input
                                  type="number"
                                  value={condition.refundPercent || 100}
                                  onChange={(e) => {
                                    setSpecialConditions(prev => ({
                                      ...prev,
                                      [key]: {
                                        ...prev[key],
                                        refundPercent: parseInt(e.target.value)
                                      }
                                    }));
                                  }}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                />
                                <span className="text-sm text-gray-600">%</span>
                              </div>
                            )}
                            {condition.fullRefund && (
                              <span className="text-sm font-medium text-green-600">Remboursement complet</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Paiements & Acomptes */}
            {activeTab === 'payments' && (
              <div className="payments-tab">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Structure des Paiements</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="payment-config bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-700 mb-4">Acompte</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Pourcentage</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={paymentStructure.deposit.percent}
                            onChange={(e) => setPaymentStructure(prev => ({
                              ...prev,
                              deposit: { ...prev.deposit, percent: parseInt(e.target.value) }
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                          <span className="text-gray-600">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Exigible</label>
                        <select
                          value={paymentStructure.deposit.dueAt}
                          onChange={(e) => setPaymentStructure(prev => ({
                            ...prev,
                            deposit: { ...prev.deposit, dueAt: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        >
                          <option value="booking">À la réservation</option>
                          <option value="confirmation">À la confirmation</option>
                          <option value="contract">À la signature</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="payment-config bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-700 mb-4">Solde</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Pourcentage</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={paymentStructure.balance.percent}
                            onChange={(e) => setPaymentStructure(prev => ({
                              ...prev,
                              balance: { ...prev.balance, percent: parseInt(e.target.value) }
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                          <span className="text-gray-600">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Exigible</label>
                        <input
                          type="text"
                          value={paymentStructure.balance.dueAt}
                          onChange={(e) => setPaymentStructure(prev => ({
                            ...prev,
                            balance: { ...prev.balance, dueAt: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder="Ex: 30 jours avant l'arrivée"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="payment-config bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-700 mb-4">Dépôt de Garantie</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Montant (€)</label>
                        <input
                          type="number"
                          value={paymentStructure.securityDeposit.amount}
                          onChange={(e) => setPaymentStructure(prev => ({
                            ...prev,
                            securityDeposit: { ...prev.securityDeposit, amount: parseInt(e.target.value) }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={paymentStructure.securityDeposit.refundable}
                          onChange={(e) => setPaymentStructure(prev => ({
                            ...prev,
                            securityDeposit: { ...prev.securityDeposit, refundable: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                        />
                        <label className="text-sm text-gray-600">Remboursable</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Placeholder pour autres onglets */}
            {activeTab === 'insurance' && (
              <div className="insurance-tab">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Assurances</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <Shield size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Section assurances à développer</p>
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="preview-tab">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Aperçu Client</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <Eye size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Aperçu de la politique côté client à développer</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Summary */}
        <div className="summary-panel w-80 bg-white border-l border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Résumé</h3>
          
          {/* Simulation de remboursement */}
          <div className="refund-simulation bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-800 mb-3">Simulation Remboursement</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-blue-700 mb-1">Montant total (€)</label>
                <input
                  type="number"
                  value={refundSimulation.totalAmount}
                  onChange={(e) => setRefundSimulation(prev => ({
                    ...prev,
                    totalAmount: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-700 mb-1">Jours avant arrivée</label>
                <input
                  type="number"
                  value={refundSimulation.daysBeforeArrival}
                  onChange={(e) => setRefundSimulation(prev => ({
                    ...prev,
                    daysBeforeArrival: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-blue-700 mb-1">Période</label>
                <select
                  value={refundSimulation.selectedPeriod}
                  onChange={(e) => setRefundSimulation(prev => ({
                    ...prev,
                    selectedPeriod: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="haute-saison">Haute Saison</option>
                  <option value="basse-saison">Basse Saison</option>
                </select>
              </div>
              <div className="pt-3 border-t border-blue-200">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-700">Remboursement:</span>
                  <span className="font-medium text-blue-800">€{calculateRefund().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions d'export */}
          <div className="export-actions">
            <h4 className="font-medium text-gray-700 mb-3">Exporter</h4>
            <div className="space-y-2">
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
                <Download size={16} />
                <span>PDF</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
                <FileText size={16} />
                <span>Excel</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
                <Mail size={16} />
                <span>Envoyer par email</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100">
                <Link size={16} />
                <span>Lien public</span>
              </button>
            </div>
          </div>

          {/* Conformité légale */}
          <div className="legal-compliance mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Conformité Légale</h4>
            <div className="space-y-2 text-sm text-green-700">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Conforme RGPD</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Droit de rétractation</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Loi française</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;